/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import {
  NOTHING_FOUND,
  foundSchema,
  placementSchema,
  previewSchema,
  type Placement,
} from '@/api/agreements';
import { request } from '@/api/client';
import { type PageSpotInput } from '@/api/counterpartyPaper';
import { type WorkflowField, workflowFieldSchema } from '@/api/fields';
import { type SheetCellInput, sheetDraftSchema } from '@/api/sheets';

/** Whose document a template is for: Amida's, or the counterparty's. */
export const PAPERS = ['company', 'counterparty'] as const;
export type Paper = (typeof PAPERS)[number];

/**
 * What every view of a workflow carries.
 *
 * The card and the detail are two different documents, not one with extra
 * fields: the card carries figures the list is sorted and compared by, the
 * detail carries the shape of the thing. Extending one from the other makes
 * the detail demand numbers the API has no reason to compute for it.
 */
const workflowBaseSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  owning_team: z.string(),
  requires_approval: z.boolean(),
  use_count: z.number(),
  has_document: z.boolean(),
  /**
   * Whose document its agreements are on, fixed for the template's life:
   * `company` for Amida's own, generated from the template; `counterparty`
   * for one the other side drafted, uploaded on each agreement -- a template
   * with no document, blanks or questions of its own. A server that predates
   * the question sends nothing, and every template then is Amida's.
   */
  paper: z.enum(PAPERS).default('company'),
  /**
   * Whether you may change this template (auth `ISO-16`): you manage
   * templates and, while it is limited to roles, hold one of them or are an
   * Admin. Its page is read-only otherwise. UX only: the server refuses every
   * change from anybody else (`API-9`). A server that predates the question
   * left that to `workflow:manage`, and the answer then is yes.
   */
  can_edit: z.boolean().default(true),
  /** "Only HR can change this template.", when you may not; null otherwise. */
  edit_note: z.string().nullable().default(null),
});

export const workflowSchema = workflowBaseSchema.extend({
  average_days: z.number(),
  approver_count: z.number(),
  company_signs: z.boolean().default(false),
  counterparty_signs: z.boolean().default(true),
  signing_order: z
    .enum(['counterparty_first', 'company_first'])
    .default('counterparty_first'),
  question_count: z.number().default(0),
  /** Somebody has unpublished changes to it. */
  has_draft: z.boolean().default(false),
  /** Amida's signature is already on the document: Amida is not asked to sign. */
  company_presigned: z.boolean().default(false),
  /**
   * Whether you have access to this template, and so may start from it
   * (AC-01). The launch list shows only these. UX only: the server refuses a
   * launch from any other (`API-9`). A server that predates the question
   * let everybody start from everything, and the answer then is yes.
   */
  can_launch: z.boolean().default(true),
  /**
   * Retired: nothing new starts from it. Listed only when asked for with
   * `includeRetired`, for the agreements already made from it (`IMP-32`).
   */
  retired: z.boolean().default(false),
});

export type Workflow = z.infer<typeof workflowSchema>;

export const workflowVersionSchema = z.object({
  version: z.number(),
  label: z.string().nullable(),
  published_at: z.string().nullable(),
  is_current: z.boolean(),
});

export type WorkflowVersion = z.infer<typeof workflowVersionSchema>;

export const versionsQuery = (workflowId: string) =>
  queryOptions({
    queryKey: ['workflow-versions', workflowId] as const,
    queryFn: ({ signal }) =>
      request(
        `/api/v1/workflows/${workflowId}/versions`,
        z.array(workflowVersionSchema),
        {
          signal,
        },
      ),
  });

/**
 * Choose which version new agreements are made from.
 *
 * Agreements already made name the version they came from and are untouched:
 * switching is a decision about the future, never a rewrite of the past.
 */
export const chooseWorkflowVersion = (workflowId: string, version: number) =>
  request(
    `/api/v1/workflows/${workflowId}/current-version`,
    z.array(workflowVersionSchema),
    { method: 'PUT', body: { version } },
  );

export const workflowDocumentSchema = z.object({
  version: z.number(),
  label: z.string().nullable(),
  store: z.string(),
  filename: z.string(),
  uploaded_at: z.string().nullable(),
  download_path: z.string(),
});

export type WorkflowDocument = z.infer<typeof workflowDocumentSchema>;

/**
 * Upload the document behind a workflow.
 *
 * Publishes it as the next version rather than replacing the current one: an
 * agreement made last March names the version it came from, and swapping the
 * file under it would quietly make that unreproducible.
 */
export const uploadWorkflowDocument = (
  workflowId: string,
  file: File,
  label?: string,
) => {
  const form = new FormData();
  form.append('file', file);
  if (label?.trim()) form.append('label', label.trim());
  return request(`/api/v1/workflows/${workflowId}/document`, workflowDocumentSchema, {
    method: 'POST',
    body: form,
  });
};

/** How each store reads on screen. */
export const STORE_LABEL: Record<string, string> = {
  local: 'this machine',
  gdrive: 'Google Drive',
  sharepoint: 'SharePoint',
  import: 'brought in by import',
};

/**
 * The document a workflow produces, rendered from what has been typed so far.
 *
 * Asked of the workflow rather than of a draft agreement, so the launch form
 * can show the document the moment it opens -- before anybody has said who the
 * agreement is with, and without creating an agreement to render against.
 * Nothing is saved -- the signature spots and the fields beside them
 * included, which are sent (each with its `kind`, and a custom field with its
 * `label`) so the server can draw them, and are stored only when the
 * agreement is created.
 */
export const previewWorkflow = (
  id: string,
  values: Record<string, string>,
  placements: readonly Placement[] = [],
) =>
  request(`/api/v1/workflows/${id}/preview`, previewSchema, {
    method: 'POST',
    body: { values, placements },
  });

/**
 * The workflows to start from. With `includeRetired`, also the retired ones
 * that have agreements, for a list's type filter (`IMP-32`). Keyed under
 * `['workflows']` either way, so invalidating that refreshes both.
 */
export const workflowsQuery = ({
  includeRetired = false,
}: { includeRetired?: boolean } = {}) =>
  queryOptions({
    queryKey: includeRetired
      ? (['workflows', { includeRetired }] as const)
      : (['workflows'] as const),
    queryFn: ({ signal }) =>
      request(
        includeRetired ? '/api/v1/workflows?include_retired=true' : '/api/v1/workflows',
        z.array(workflowSchema),
        { signal },
      ),
  });

export { workflowFieldSchema, type WorkflowField };

export const workflowDetailSchema = workflowBaseSchema.extend({
  approval_exemption_reason: z.string().nullable(),
  company_signs: z.boolean(),
  counterparty_signs: z.boolean(),
  signing_order: z.enum(['counterparty_first', 'company_first']),
  /** Amida's signature is already on the document, so nobody signs for Amida. */
  company_presigned: z.boolean().default(false),
  version: z.number(),
  fields: z.array(workflowFieldSchema),
  approvers: z.array(z.object({ name: z.string(), initials: z.string() })),
});

export type WorkflowDetail = z.infer<typeof workflowDetailSchema>;

/**
 * Start a template. `paper` is asked first and never again: a template for
 * the counterparty's document has no document, blanks or questions, and its
 * draft is opened with it, so it can be published as soon as it is made.
 */
export const createWorkflow = (body: {
  name: string;
  description?: string | undefined;
  owning_team: string;
  requires_approval: boolean;
  approval_exemption_reason?: string | undefined;
  paper?: Paper | undefined;
}) => request('/api/v1/workflows', workflowDetailSchema, { method: 'POST', body });

/** A template's name, as the server saved it: trimmed, runs of spaces made one (WF-85). */
export const workflowNameSchema = z.object({
  workflow_id: z.string(),
  name: z.string(),
});

export type WorkflowName = z.infer<typeof workflowNameSchema>;

/** The longest a template's name may be: the column's width. */
export const WORKFLOW_NAME_MOST = 255;

/** The name as the server will keep it: trimmed, each run of spaces made one (WF-85). */
export const tidyName = (name: string): string => name.replace(/\s+/g, ' ').trim();

/**
 * Rename a template (WF-84). Applies at once, outside the draft; a name
 * another template in use has is refused 409 `name_in_use`.
 */
export const renameWorkflow = (id: string, name: string) =>
  request(`/api/v1/workflows/${id}/name`, workflowNameSchema, {
    method: 'PUT',
    body: { name },
  });

export const workflowQuery = (id: string) =>
  queryOptions({
    queryKey: ['workflow', id] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/workflows/${id}`, workflowDetailSchema, { signal }),
  });

export const removedWorkflowSchema = z.object({
  id: z.string(),
  name: z.string(),
  outcome: z.enum(['deleted', 'retired']),
  agreements: z.number(),
  /** Packets in progress with a step from it still to start (ADW-5498). */
  packets_waiting: z.number().default(0),
});

export type RemovedWorkflow = z.infer<typeof removedWorkflowSchema>;

export const workflowUsageSchema = z.object({
  agreements: z.number(),
  /** The packet templates that name it as a step. */
  packet_templates: z.array(z.string()),
  /** Packets in progress still waiting to start a step from it. */
  packets_waiting: z.number(),
});

export type WorkflowUsage = z.infer<typeof workflowUsageSchema>;

/**
 * What deleting a template would affect, for its confirmation (ADW-5498):
 * agreements made from it, packet templates naming it, and packets in progress
 * still waiting to start it. Only for those who may delete it.
 */
export const workflowUsageQuery = (workflowId: string) =>
  queryOptions({
    queryKey: ['workflow-usage', workflowId] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/workflows/${workflowId}/usage`, workflowUsageSchema, { signal }),
  });

/**
 * Remove a workflow.
 *
 * A workflow nothing was ever made from is deleted. One that has produced
 * agreements is retired instead — an agreement names the version it came from,
 * and an agreement nobody can reproduce is worse than a workflow nobody can
 * launch. The server decides which; this only asks.
 */
export const removeWorkflow = (workflowId: string) =>
  request(`/api/v1/workflows/${workflowId}`, removedWorkflowSchema, { method: 'DELETE' });

export const workflowPreviewSchema = z.object({
  version: z.number(),
  body: z.string(),
  fields: z.array(z.string()),
});

/**
 * The workflow's own words, with its blanks marked.
 *
 * The body is HTML the server built: every word of the document is escaped
 * before the marks are added, so a workflow containing angle brackets is text
 * rather than markup.
 */
export const workflowPreviewQuery = (workflowId: string) =>
  queryOptions({
    queryKey: ['workflow-preview', workflowId] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/workflows/${workflowId}/preview`, workflowPreviewSchema, {
        signal,
      }),
    retry: false,
  });

export interface FieldEdit {
  key: string;
  label: string;
  field_type: WorkflowField['field_type'];
  is_required: boolean;
  placeholder?: string | null;
  /** The agreement's effective date, end date or value, if it is one (`LC-11`). */
  role?: WorkflowField['role'];
  /** Which dates a date question takes; any, where omitted. */
  date_rule?: WorkflowField['date_rule'];
}

/**
 * Rename and retype the questions.
 *
 * Not *which* questions: those come from the placeholders in the document, and
 * the server refuses a key the document does not mention.
 */
export const describeFields = (workflowId: string, edits: FieldEdit[]) =>
  request(`/api/v1/workflows/${workflowId}/fields`, z.array(workflowFieldSchema), {
    method: 'PUT',
    body: edits,
  });

export const signingSchema = z.object({
  company_signs: z.boolean(),
  counterparty_signs: z.boolean(),
  signing_order: z.enum(['counterparty_first', 'company_first']),
  /**
   * "Amida has already signed this document": its signature is a picture in
   * the template, so Amida is never asked to sign. The server keeps
   * `company_signs` false while this is on.
   */
  company_presigned: z.boolean().default(false),
});

export type Signing = z.infer<typeof signingSchema>;

/**
 * Who signs what comes out of this workflow, and who is asked first.
 *
 * Not *who*: Amida's signer is chosen when an agreement is launched and the
 * counterparty's email is typed then, because both differ every time.
 */
export const setSigning = (workflowId: string, signing: Signing) =>
  request(`/api/v1/workflows/${workflowId}/signing`, signingSchema, {
    method: 'PUT',
    body: signing,
  });

/**
 * Replace the approvers, in the order they are asked.
 *
 * An empty list means no approval step — which is the default. Approvers are
 * people who sign in to Vellum: approving is a decision on the record, not a
 * reply to an email.
 */
export const setApprovers = (workflowId: string, people: string[]) =>
  request(
    `/api/v1/workflows/${workflowId}/approvers`,
    z.array(z.object({ name: z.string(), initials: z.string() })),
    { method: 'PUT', body: people },
  );

/* ── The document, as something a person can edit ─────────────────────────── */

/**
 * One run of words, or a blank.
 *
 * `signs` says the words are a party's signature line -- what an Ironclad
 * signature token became when the document was read in. A hint the launch form
 * offers a spot on; it writes nothing into the document. It travels through the
 * editor and back so opening a document and saving it does not erase it.
 */
export const runSchema = z.object({
  text: z.string().nullable().default(null),
  bold: z.boolean().default(false),
  italic: z.boolean().default(false),
  underline: z.boolean().default(false),
  blank: z.string().nullable().default(null),
  signs: z.enum(['company', 'counterparty']).nullable().default(null),
});

/**
 * One block of the document. A `table` carries `rows`: each row a list of
 * cells, each cell a line of runs as a paragraph is, with `header` saying the
 * first row is the table's heading row.
 *
 * An `image` block is a picture read out of the Word file the template was
 * uploaded with -- most often Amida's signature, already on the document. It
 * is named by the SHA-256 of its bytes (`image`) and drawn at `width` by
 * `height` EMUs; the editor may keep it or delete it, never add one.
 */
export const blockSchema = z.object({
  type: z.enum(['paragraph', 'list', 'signature', 'table', 'image']),
  runs: z.array(runSchema).default([]),
  items: z.array(z.array(runSchema)).default([]),
  rows: z.array(z.array(z.array(runSchema))).default([]),
  header: z.boolean().default(false),
  level: z.number().default(0),
  align: z.enum(['left', 'center', 'right', 'justify']).default('left'),
  ordered: z.boolean().default(false),
  party: z.enum(['company', 'counterparty']).nullable().default(null),
  image: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .nullable()
    .default(null),
  width: z.number().nullable().default(null),
  height: z.number().nullable().default(null),
});

/** Where a picture in a template's document is read from: through Vellum, never the store. */
export const templateImageUrl = (digest: string): string =>
  `/api/v1/template-images/${encodeURIComponent(digest)}`;

export const workflowDocumentContentSchema = z.object({
  version: z.number(),
  blocks: z.array(blockSchema),
  keys: z.array(z.string()),
  not_carried: z.array(z.string()).default([]),
});

export type Run = z.infer<typeof runSchema>;
export type Block = z.infer<typeof blockSchema>;
export type WorkflowDocumentContent = z.infer<typeof workflowDocumentContentSchema>;

/**
 * The document behind the version in use, as blocks and blanks.
 *
 * A workflow uploaded before the editor existed has no editable document and
 * answers 404. That is not an error to recover from — the Word file is still
 * what generation fills — so the screen offers to upload it again rather than
 * showing a failure.
 */
export const workflowDocumentQuery = (workflowId: string) =>
  queryOptions({
    queryKey: ['workflow', workflowId, 'document'] as const,
    queryFn: ({ signal }) =>
      request(
        `/api/v1/workflows/${workflowId}/document/content`,
        workflowDocumentContentSchema,
        { signal },
      ),
    // A workflow with no editable document is an answer, not a failure worth
    // asking three times for.
    retry: false,
  });

/**
 * Save the document. The blanks in it decide which questions get asked.
 *
 * `fieldKinds` says what kind of answer a question *being created* takes — a
 * date, a number, a paragraph — as chosen when the blank was marked. It is
 * ignored for a question that already exists, whose kind belongs to the field:
 * a correction made on the Create tab must not be undone by the next edit to
 * the document.
 */
export const saveWorkflowDocument = (
  workflowId: string,
  blocks: Block[],
  fieldKinds: Record<string, string> = {},
) =>
  request(
    `/api/v1/workflows/${workflowId}/document/content`,
    workflowDocumentContentSchema,
    { method: 'PUT', body: { blocks, field_kinds: fieldKinds } },
  );

/* ── Editing as a draft: Cancel or Publish ─────────────────────────────────── */

export const STEP_KEYS = ['document', 'create', 'review', 'sign'] as const;
export type StepKey = (typeof STEP_KEYS)[number];

export const draftProblemSchema = z.object({
  step: z.enum(STEP_KEYS),
  message: z.string(),
});

export type DraftProblem = z.infer<typeof draftProblemSchema>;

/**
 * Where the template is signed, as the editor is offered it (`templates/spec.md`
 * §12): the spots kept, when placed on this document or still fitting it;
 * whether they were placed on this very document (`current`) -- false with
 * spots, the document changed since and they want checking and Done again;
 * and which document a spot placed now points into.
 */
export const templateSpotsSchema = z.object({
  placements: z.array(placementSchema),
  current: z.boolean(),
  document_digest: z.string().nullable(),
});

export type TemplateSpots = z.infer<typeof templateSpotsSchema>;

const NO_TEMPLATE_SPOTS: TemplateSpots = {
  placements: [],
  current: false,
  document_digest: null,
};

export const documentUnreadSchema = z.object({
  code: z.literal('not_read_in'),
  message: z.string(),
});

export const workflowDraftSchema = z.object({
  workflow_id: z.string(),
  version: z.number(),
  published_version: z.number(),
  exists: z.boolean(),
  changed: z.array(z.enum(STEP_KEYS)),
  problems: z.array(draftProblemSchema),
  has_document: z.boolean(),
  document: workflowDocumentContentSchema.nullable(),
  fields: z.array(workflowFieldSchema),
  approvers: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      initials: z.string(),
      email: z.string(),
      is_active: z.boolean(),
    }),
  ),
  signing: signingSchema,
  label: z.string().nullable(),
  not_carried: z.array(z.string()).default([]),
  spots: templateSpotsSchema.default(NO_TEMPLATE_SPOTS),
  /** Whose document it is for; `counterparty` has no document or questions. */
  paper: z.enum(PAPERS).default('company'),
  /**
   * Why there is a file but no document to edit, with the server's words for
   * what to do. Null when there is a document, or no file.
   */
  unread: documentUnreadSchema.nullable().default(null),
  /** `xlsx`: an Excel template, whose questions are cells (templates spec §16). */
  document_kind: z.enum(['docx', 'xlsx']).default('docx'),
  /** An Excel template's sheet: its question cells and where it is signed. */
  sheet: sheetDraftSchema.nullable().default(null),
});

export type WorkflowDraft = z.infer<typeof workflowDraftSchema>;

/**
 * The template as its editor shows it: the unpublished changes if anybody has
 * made any, otherwise what is published. Reading never creates a draft.
 */
export const workflowDraftQuery = (workflowId: string) =>
  queryOptions({
    queryKey: ['workflow', workflowId, 'draft'] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/workflows/${workflowId}/draft`, workflowDraftSchema, { signal }),
  });

/** Keep an edit to the document in the draft. Its blanks decide the questions. */
export const writeDraftDocument = (
  workflowId: string,
  blocks: Block[],
  fieldKinds: Record<string, string> = {},
) =>
  request(`/api/v1/workflows/${workflowId}/draft/document`, workflowDraftSchema, {
    method: 'PUT',
    body: { blocks, field_kinds: fieldKinds },
  });

/** Put a new Word file or Excel workbook into the draft. Published only when the draft is. */
export const uploadDraftDocument = (workflowId: string, file: File, label?: string) => {
  const form = new FormData();
  form.append('file', file);
  if (label?.trim()) form.append('label', label.trim());
  return request(`/api/v1/workflows/${workflowId}/draft/document`, workflowDraftSchema, {
    method: 'POST',
    body: form,
  });
};

/** Keep a change to the questions in the draft. */
export const writeDraftFields = (workflowId: string, edits: FieldEdit[]) =>
  request(`/api/v1/workflows/${workflowId}/draft/fields`, workflowDraftSchema, {
    method: 'PUT',
    body: edits,
  });

/** Keep a change to who approves in the draft, in the order they were chosen. */
export const writeDraftApprovers = (workflowId: string, people: string[]) =>
  request(`/api/v1/workflows/${workflowId}/draft/approvers`, workflowDraftSchema, {
    method: 'PUT',
    body: people,
  });

/** Keep a change to who signs in the draft. */
export const writeDraftSigning = (workflowId: string, signing: Signing) =>
  request(`/api/v1/workflows/${workflowId}/draft/signing`, workflowDraftSchema, {
    method: 'PUT',
    body: signing,
  });

/** Apply every change in the draft at once, as the template's next version. */
export const publishWorkflow = (workflowId: string) =>
  request(
    `/api/v1/workflows/${workflowId}/publish`,
    z.object({ version: z.number(), changed: z.array(z.enum(STEP_KEYS)) }),
    { method: 'POST' },
  );

/**
 * Throw every unpublished change away. A template never published is deleted
 * with them, so starting one and cancelling leaves nothing behind.
 */
export const discardWorkflowDraft = (workflowId: string) =>
  request(
    `/api/v1/workflows/${workflowId}/draft`,
    z.object({ outcome: z.enum(['discarded', 'deleted']) }),
    { method: 'DELETE' },
  );

/* ── An Excel template's cells and spots (templates spec §16) ─────────────── */

/**
 * Keep which cells are questions in the draft, replacing what it had: each
 * cell's address and the question filling it. The questions follow the cells.
 */
export const writeSheetCells = (workflowId: string, cells: readonly SheetCellInput[]) =>
  request(`/api/v1/workflows/${workflowId}/draft/sheet/cells`, workflowDraftSchema, {
    method: 'PUT',
    body: { cells },
  });

/** Keep where the sheet is signed: boxes on its PDF's pages, and how many pages it had. */
export const writeSheetSpots = (
  workflowId: string,
  spots: readonly PageSpotInput[],
  pages: number,
) =>
  request(`/api/v1/workflows/${workflowId}/draft/sheet/spots`, workflowDraftSchema, {
    method: 'PUT',
    body: { spots, pages },
  });

/* ── Where it is signed, set in the template ──────────────────────────────── */

export const spotsPreviewSchema = z.object({
  html: z.string(),
  document_digest: z.string(),
  suggested: z.array(placementSchema).default([]),
  found: foundSchema.default(NOTHING_FOUND),
});

export type SpotsPreview = z.infer<typeof spotsPreviewSchema>;

/**
 * The template's document -- the draft's, else the published one -- drawn for
 * placing its signing spots, with the spots placed so far and every blank
 * open. Writes nothing.
 */
export const previewDraftSpots = (workflowId: string, placements: readonly Placement[]) =>
  request(`/api/v1/workflows/${workflowId}/draft/spots/preview`, spotsPreviewSchema, {
    method: 'POST',
    body: { placements },
  });

/**
 * Keep where the template is signed in the draft, with the digest of the
 * document they were placed on. Published only when the draft is.
 */
export const writeDraftSpots = (
  workflowId: string,
  placements: readonly Placement[],
  documentDigest: string,
) =>
  request(`/api/v1/workflows/${workflowId}/draft/spots`, workflowDraftSchema, {
    method: 'PUT',
    body: { placements, document_digest: documentDigest },
  });

export const spotsInUseSchema = z.object({
  placements: z.array(placementSchema),
  document_digest: z.string().nullable(),
});

export type SpotsInUse = z.infer<typeof spotsInUseSchema>;

/**
 * The spots the version in use places: what the launch form starts an
 * agreement with. Empty for a template that places none.
 */
export const templateSpotsQuery = (workflowId: string) =>
  queryOptions({
    queryKey: ['workflow', workflowId, 'spots'] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/workflows/${workflowId}/spots`, spotsInUseSchema, { signal }),
  });
